import classNames from "classnames";
import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { useChildrenArray } from "../../../lib/helpers/hooks/useChildrenArray";
import {
  AttachmentIcon,
  CalendarEmptyIcon,
  DownloadIcon,
  FileIcon,
  FilterIcon,
  SortIcon,
} from "../../AscIcons";
import AscSearchInput from "../../AscInputFields/AscSearchInput/AscSearchInput";
import AscHeading from "../../AscTypography/AscHeading/AscHeading";
import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";
import styles from "./AscCustomSidebar.module.scss";
import AscCustomSidebarItem, {
  AscCustomSidebarItemBasicProps,
} from "./AscCustomSidebarItem/AscCustomSidebarItem";

export interface AscCustomSidebarProps {
  className?: string;
  children: JSX.Element[] | any;
  selectedId?: string | null;
  onSelectionChange?: (activeItem: AscCustomSidebarItemBasicProps) => void;
}

function AscCustomSidebar({
  className,
  children,
  selectedId,
  onSelectionChange,
}: AscCustomSidebarProps) {
  const sidebarItems =
    useChildrenArray<AscCustomSidebarItemBasicProps>(children);
  const [activeId, setActiveId] = useState<string>();

  const activeContent = sidebarItems?.find(
    (sidebarItem) => sidebarItem.id === activeId
  );

  const content = activeContent?.children;

  function handleActiveChange(activeItem: AscCustomSidebarItemBasicProps) {
    if (activeItem?.disabled) {
      return;
    }
    setActiveId(activeItem?.id);

    onSelectionChange?.(activeItem);
  }

  useEffect(() => {
    if (selectedId === undefined && sidebarItems?.length) {
      setActiveId(sidebarItems[0]?.id);
    }

    const itemToSelect = sidebarItems?.find(
      (sidebarItem) => sidebarItem.id === selectedId
    );

    if (!itemToSelect || itemToSelect?.id === activeId) {
      return;
    }

    handleActiveChange(itemToSelect);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, sidebarItems]);

  return (
    <div
      data-testid="asc-sidebar"
      className={classNames(styles.ground, className)}
    >
      <div className={styles.sidebar}>
        <div className={styles.topNav}>
          <AscSearchInput className={styles.searchInput} />
          <span className={styles.iconsField}>
            <SortIcon />
          </span>
          <span className={styles.iconsField}>
            <FilterIcon />
          </span>
        </div>

        <ul className={styles.sideBarItems}>
          {sidebarItems.map((sidebarItem) => {
            return (
              <li
                key={sidebarItem?.id}
                onClick={() => {
                  handleActiveChange(sidebarItem);
                }}
                className={classNames(styles.sidebarItem, {
                  [styles.disabled]: sidebarItem?.disabled,
                  [styles.active]: activeId === sidebarItem?.id,
                })}
              >
                <div className={styles.content}>
                  <AscHeading className={styles.title} level={6}>
                    {sidebarItem?.name}
                  </AscHeading>
                  <div className={styles.dateContent}>
                    <span>
                      <CalendarEmptyIcon className={styles.icon} />
                      {dayjs(sidebarItem?.date).format("D MMM[,]  YYYY")}
                    </span>

                    {sidebarItem?.pin === true && (
                      <span>
                        <AttachmentIcon />
                      </span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className={styles.mainGround}>
        {activeContent && (
          <div className={styles.content}>
            <AscHeading className={styles.header}>{content.header}</AscHeading>

            <AscParagraph className={styles.text}>{content.text}</AscParagraph>

            <AscHeading className={styles.subHeader}>
              {content.subHeader}
            </AscHeading>

            {content.file && (
              <div className={styles.file}>
                <FileIcon
                  className={classNames(styles.icon, styles.fileIcon)}
                />
                <div className={styles.fileDetails}>
                  <span className={styles.name}>{content.file.name}</span>
                  <span className={styles.size}>{content.file.size}</span>
                </div>
                <DownloadIcon
                  className={classNames(styles.icon, styles.downloadIcon)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

AscCustomSidebar.Item = AscCustomSidebarItem;

export default AscCustomSidebar;
